import {
  createContext,
  useContext,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../../../api/axios";
import type { ApiResponseEnvelope } from "../../../types/auth";

type Identifier = number | string;

type FacilityRole = {
  id: Identifier;
  role_name: string;
};

type TechnicianUser = {
  id: Identifier;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  user_type?: string;
  profile_image_full_path?: string;
  is_active?: boolean;
  ref_code?: string;
  created_at?: string;
  roles?: FacilityRole[];
};

type TechnicianListPayload = {
  data?: TechnicianUser[];
  total?: number;
  current_page?: number;
};

type FacilityManagementContextValue = {
  technicianStats: {
    total: number;
    active: number;
    inactive: number;
  };
  technicians: TechnicianUser[];
  availableRoles: FacilityRole[];
  isTechniciansLoading: boolean;
  isRolesLoading: boolean;
  refreshFacilitySharedData: () => Promise<void>;
};

const facilityManagementQueryKeys = {
  technicians: ["facility-management", "technicians", "shared"] as const,
  roles: ["facility-management", "roles"] as const,
};

const FacilityManagementContext =
  createContext<FacilityManagementContextValue | null>(null);

type FacilityManagementProviderProps = {
  children: ReactNode;
};

export const FacilityManagementProvider = ({
  children,
}: FacilityManagementProviderProps) => {
  const queryClient = useQueryClient();

  const techniciansQuery = useQuery({
    queryKey: facilityManagementQueryKeys.technicians,
    queryFn: async () => {
      const isProvider = localStorage.getItem('userRole') === 'provider';
      const baseUrl = isProvider ? "/v1/provider/facility" : "/v1/admin/facility";
      
      const response = await axios.get<ApiResponseEnvelope<TechnicianListPayload>>(
        `${baseUrl}/technician`,
      );

      return response.data.content || {};
    },
  });

  const rolesQuery = useQuery({
    queryKey: facilityManagementQueryKeys.roles,
    queryFn: async () => {
      const isProvider = localStorage.getItem('userRole') === 'provider';
      const baseUrl = isProvider ? "/v1/provider/facility" : "/v1/admin/facility";

      const response = await axios.get<ApiResponseEnvelope<FacilityRole[]>>(
        `${baseUrl}/roles`,
      );

      return response.data.content || [];
    },
  });

  const technicians = techniciansQuery.data?.data || [];
  const total = Number(techniciansQuery.data?.total ?? technicians.length ?? 0);
  const active = technicians.filter((technician) => technician.is_active).length;
  const inactive = Math.max(0, total - active);

  const refreshFacilitySharedData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: facilityManagementQueryKeys.technicians,
      }),
      queryClient.invalidateQueries({
        queryKey: facilityManagementQueryKeys.roles,
      }),
    ]);
  };

  return (
    <FacilityManagementContext.Provider
      value={{
        technicianStats: {
          total,
          active,
          inactive,
        },
        technicians,
        availableRoles: rolesQuery.data || [],
        isTechniciansLoading:
          techniciansQuery.isLoading || techniciansQuery.isFetching,
        isRolesLoading: rolesQuery.isLoading || rolesQuery.isFetching,
        refreshFacilitySharedData,
      }}
    >
      {children}
    </FacilityManagementContext.Provider>
  );
};

export const useFacilityManagementContext = () => {
  const context = useContext(FacilityManagementContext);

  if (!context) {
    throw new Error(
      "useFacilityManagementContext must be used within FacilityManagementProvider",
    );
  }

  return context;
};

export { facilityManagementQueryKeys };
